/**
 * ADR-125 — "Mine / All" scope filter for the Business Unit tree on the
 * Select Business Unit step of the Schedule Feedback Session wizard.
 *
 * This module is a VIEW FILTER, not authorization. `GET /clusters/:id/business-unit-hierarchy`
 * stays unscoped and the schedule endpoints keep accepting any valid businessUnitId from any
 * caller. Do not cite it as a permission gate — that is still owed by the phase-2 ADR (ADR-003).
 *
 * Pure: no React, no network, no auth. Everything is a function of data the caller already
 * holds — the hierarchy payload and the `memberOfBusinessUnits` / `managedBusinessUnits`
 * arrays from `GET /me`.
 */

import { scopeTree } from './treeScope';

export const BU_SCOPE = Object.freeze({
  MINE: 'mine',
  ALL: 'all',
});

/**
 * Seed ids across ALL Clusters, per `GET /me` (ADR-125 Decision 2):
 *   - `memberOfBusinessUnits` — BU of the project of every active allocation of the
 *     employee, plus of every active allocation of their Manager-of-Record reports
 *     (ADR-125 Decision 10). `[{ id, clusterId }]`, no descendants.
 *   - `managedBusinessUnits` — BUs the employee owns (`BusinessUnit.ownerId`), each with
 *     its full descendant list already flattened into `childBus`.
 *
 * Narrowing to the selected Cluster is NOT done here: `scopeBusinessUnitTree` ignores any
 * seed absent from the fetched tree (other Cluster, ADR-120-ended, moved, stale cache).
 *
 * @param {object} [employee] the `/me` payload
 * @returns {Set<string>} seed ids, possibly empty
 */
export const resolveMyBusinessUnitIds = (employee) => {
  const ids = new Set();
  (employee?.memberOfBusinessUnits ?? []).forEach((bu) => {
    if (bu?.id) ids.add(bu.id);
  });
  (employee?.managedBusinessUnits ?? []).forEach((bu) => {
    if (bu?.id) ids.add(bu.id);
    (bu?.childBus ?? []).forEach((child) => {
      if (child?.id) ids.add(child.id);
    });
  });
  return ids;
};

/**
 * Flatten a nested `childBus` tree to `{ id, parentId }` rows. The parent is taken from the
 * STRUCTURE (the tree we are about to draw), not from the `parentBuId` field. Guarded against
 * a node appearing twice.
 */
const flatten = (root) => {
  const rows = [];
  const seen = new Set();
  const stack = [{ node: root, parentId: null }];
  while (stack.length) {
    const { node, parentId } = stack.pop();
    if (!node?.id || seen.has(node.id)) continue;
    seen.add(node.id);
    rows.push({ id: node.id, parentId });
    (node.childBus ?? []).forEach((child) => stack.push({ node: child, parentId: node.id }));
  }
  return rows;
};

/**
 * Narrow the nested Business Unit tree of one Cluster to a scoped, renderable tree
 * (ADR-125 Decision 3): seed → descendant expansion → ancestor closure.
 *
 * Returns a PRUNED COPY — the input is never mutated. Context (ancestor-only) nodes carry
 * `isContext: true`. Because of ancestor closure the returned tree's root is always the
 * input root, so `d3.hierarchy` gets the same single-root shape as today.
 *
 * @param {object|null} root the tree root (`getBusinessUnitHierarchy(...)[0]`)
 * @param {Set<string>|null|undefined} seedIds from `resolveMyBusinessUnitIds`
 * @returns {{ tree: object|null, selectableIds: Set<string>|null, seedCount: number }}
 *          `selectableIds === null` means "no seed in this tree — render unscoped".
 */
export const scopeBusinessUnitTree = (root, seedIds) => {
  if (!root) return { tree: root ?? null, selectableIds: null, seedCount: 0 };

  const { visibleIds, selectableIds, seedCount } = scopeTree(
    flatten(root),
    seedIds,
    (row) => row.id,
    (row) => row.parentId
  );
  if (!selectableIds) return { tree: root, selectableIds: null, seedCount: 0 };

  const seen = new Set();
  const prune = (node) => {
    seen.add(node.id);
    return {
      ...node,
      isContext: !selectableIds.has(node.id),
      childBus: (node.childBus ?? [])
        .filter((child) => child?.id && visibleIds.has(child.id) && !seen.has(child.id))
        .map(prune),
    };
  };

  return { tree: prune(root), selectableIds, seedCount };
};

/**
 * ADR-125 Decision 11 — in the wizard, the Cluster's root Business Unit is selectable only
 * by the employee who owns it, in both scopes. Ownership is read from the freshly fetched
 * tree (`root.owner.id`), not from the cached `/me`.
 *
 * @param {object|null} rootNode the depth-0 node (raw or d3-transformed; must carry `owner`)
 * @param {string|null|undefined} currentEmployeeId
 * @returns {boolean}
 */
export const isRootSelectableBy = (rootNode, currentEmployeeId) =>
  Boolean(currentEmployeeId) && rootNode?.owner?.id === currentEmployeeId;
